import React from 'react';
import { bool, number, string, object } from 'prop-types';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { SUPPORTED_FIVERR_LOCALES_KEYS } from '@fiverr-private/localization';
import * as Links from '../Links';
import { SwitchToFiverrLink } from '../Links/SwitchToFiverrLink';
import { BecomeSellerLink } from '../Links/BecomeSellerLink';
import { BecomeSellerJobPageLink } from '../Links/BecomeSellerJobPageLink';
import { ShareIdeas } from '../Links/ShareIdeasLink';
import { bigQuerySeller } from '../SellerLinks';

export const bigQueryBuyer = (id, type) => ({
    group: 'header_clicks',
    type: `clicked_header.${type}`,
    user: {
        id,
        navigation_mode: 'buying',
    },
});

export const BuyerSwitchToFiverrLink = ({ buyerId }) => (
    <SwitchToFiverrLink bigQueryData={bigQueryBuyer(buyerId, 'go_to_fiverr')} />
);

BuyerSwitchToFiverrLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerBecomeSellerLink = ({ buyerId, source }) => {
    const { locale } = getContext();
    const shouldRouteToSellerJobPages = [
        SUPPORTED_FIVERR_LOCALES_KEYS.DE_DE,
        SUPPORTED_FIVERR_LOCALES_KEYS.ES,
        SUPPORTED_FIVERR_LOCALES_KEYS.FR_FR,
    ].includes(locale);

    return shouldRouteToSellerJobPages ? (
        <BecomeSellerJobPageLink bigQueryData={bigQueryBuyer(buyerId, 'start_selling')} source={source} />
    ) : (
        <BecomeSellerLink bigQueryData={bigQueryBuyer(buyerId, 'start_selling')} source={source} />
    );
};

BuyerBecomeSellerLink.propTypes = {
    buyerId: number.isRequired,
    source: string,
};

export const BuyerListsLink = ({ className, tooltipClassName, buyerId, showAsIcon }) => (
    <Links.ListsLink
        className={className}
        tooltipClassName={tooltipClassName}
        bigQueryData={bigQueryBuyer(buyerId, 'favorites')}
        showAsIcon={showAsIcon}
    />
);

BuyerListsLink.propTypes = {
    className: string,
    tooltipClassName: string,
    buyerId: number,
    showAsIcon: bool,
};

export const BuyerProfileLink = ({ buyerId }) => {
    const buyerProfileMixpanelData = {
        eventName: 'Clicked header menu item',
        eventData: {
            'Buyer ID': buyerId,
            Item: 'my_buying_profile',
        },
    };

    return (
        <Links.BuyerProfileLink
            mpData={buyerProfileMixpanelData}
            bigQueryData={bigQueryBuyer(buyerId, 'my_buying_profile')}
        />
    );
};

BuyerProfileLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerDashboardLink = ({ buyerId, buyerUsername }) => {
    const path = pathfinder('user_todos', {
        user_id: buyerUsername,
    });

    return <Links.UserDashboardLink bigQueryData={bigQueryBuyer(buyerId, 'my_dashboard')} href={path} />;
};

BuyerDashboardLink.propTypes = {
    buyerId: number.isRequired,
    buyerUsername: string.isRequired,
};

export const BuyerPostBuyerRequestsLink = ({ buyerId, buyerUsername }) => {
    const type = 'clicked_on_create_a_brief';

    const briefAndMatchBigQueryData = {
        ...bigQueryBuyer(buyerId, type),
        type,
        group: 'buyer_request',
        feature: {
            name: 'brief_and_match',
        },
        event_source: 'header_menu',
    };

    const briefAndMatchMixpanelData = {
        eventName: 'Clicked header menu item',
        eventData: {
            'Buyer ID': buyerId,
            Item: 'create_a_brief',
        },
    };

    return (
        <Links.UserCreateBriefAndMatchLink
            username={buyerUsername}
            bigQueryData={briefAndMatchBigQueryData}
            mpData={briefAndMatchMixpanelData}
        />
    );
};

BuyerPostBuyerRequestsLink.propTypes = {
    buyerId: number.isRequired,
    buyerUsername: string.isRequired,
};

export const BuyerReferalLink = ({ buyerId }) => (
    <Links.ReferralLink bigQueryData={bigQueryBuyer(buyerId, 'refer_a_friend')} />
);

BuyerReferalLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerAccountSettingsLink = ({ buyerId, buyerUsername }) => (
    <Links.UserAccountSettingsLink username={buyerUsername} bigQueryData={bigQueryBuyer(buyerId, 'settings')} />
);

BuyerAccountSettingsLink.propTypes = {
    buyerId: number.isRequired,
    buyerUsername: string.isRequired,
};

export const BuyerBillingLink = ({ buyerId }) => <Links.BillingLink bigQueryData={bigQueryBuyer(buyerId, 'billing')} />;

BuyerBillingLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerHelpLink = ({ buyerId }) => (
    <Links.HelpLink userType="buyer_seller" bigQueryData={bigQueryBuyer(buyerId, 'help')} />
);

BuyerHelpLink.propTypes = {
    buyerId: number.isRequired,
};

export const ShareIdeasLink = ({ buyerId }) => <ShareIdeas bigQueryData={bigQueryBuyer(buyerId, 'share_ideas')} />;

ShareIdeasLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerEventsLink = ({ buyerId }) => (
    <Links.EventsLink bigQueryData={bigQueryBuyer(buyerId, 'community_events')} />
);

BuyerEventsLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerBlogLink = ({ buyerId }) => (
    <Links.BlogLink bigQueryData={bigQueryBuyer(buyerId, 'community_blogs')} />
);

BuyerBlogLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerForumLink = ({ buyerId }) => (
    <Links.ForumLink bigQueryData={bigQueryBuyer(buyerId, 'community_forum')} />
);

BuyerForumLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerCommunityStandardsLink = ({ buyerId }) => (
    <Links.CommunityStandardsLink bigQueryData={bigQueryBuyer(buyerId, 'community_standards')} />
);

BuyerCommunityStandardsLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerPodcastLink = ({ buyerId }) => (
    <Links.PodcastLink bigQueryData={bigQueryBuyer(buyerId, 'community_podcast')} />
);

BuyerPodcastLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerContactsLink = ({ buyerId }) => (
    <Links.ContactsLink query={{ filter: 'sellers' }} bigQueryData={bigQueryBuyer(buyerId, 'buyer_contact')} />
);

BuyerContactsLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerOrdersLink = ({ buyerId, buyerUsername }) => (
    <Links.UserOrdersLink username={buyerUsername} bigQueryData={bigQueryBuyer(buyerId, 'buying_orders')} />
);

BuyerOrdersLink.propTypes = {
    buyerId: number.isRequired,
    buyerUsername: string.isRequired,
};

export const BuyerSwitchToSellerLink = ({ buyerId, buyerUsername }) => (
    <Links.SwitchToSellerLink username={buyerUsername} bigQueryData={bigQueryBuyer(buyerId, 'switch_to_selling')} />
);

BuyerSwitchToSellerLink.propTypes = {
    buyerId: number.isRequired,
    buyerUsername: string.isRequired,
};

export const BuyerYourBriefsLink = () => <Links.YourBriefsLink />;

export const BuyerResellingDashboardLink = ({ buyerId }) => {
    const path = pathfinder('reselling_buyer_dashboard');

    return (
        <Links.ResellingBuyerDashboardLink
            href={path}
            bigQueryData={bigQuerySeller(buyerId, 'reselling_buyer_dashboard')}
        />
    );
};

BuyerResellingDashboardLink.propTypes = {
    buyerId: number.isRequired,
};

export const BuyerBriefCreationLink = ({ currency }) => <Links.BriefCreationLink currency={currency} />;

BuyerBriefCreationLink.propTypes = {
    currency: object.isRequired,
};
